import { useCMSPageField } from '@/cms/fields/hooks'
import { useIntl } from '@/intl'
import getUserRatingLabelIntlId from '@/scopes/common/components/utils/getUserRatingLabelIntlId'
import { mqMin, space, tok, tokPx } from '@sunwebgroup/ui-components'
import { focusOutline, Icon, Sheet, Typography } from '@sunwebgroup/ui-components/uds'
import React from 'react'
import { css, styled } from 'styled-components'

const HeaderRightSection = styled.div`
  padding-left: ${space('primitives_space_16')};
  display: flex;
  flex-direction: column;
`
const RatingBadgeWrapper = styled.div``

const RatingBadge = styled(Typography)<{ $isHeroSection: boolean }>`
  ${Typography.modVariant('display-g-regular')};
  color: ${tok('primitives_color_success_500')};
  background: ${tok('primitives_color_success_50')};
  border-radius: ${tokPx('primitives_corner_radius_l')};
  padding: ${space('primitives_space_16')} ${space('primitives_space_12')};
  display: block;
  min-width: ${space('primitives_space_64')};
  text-align: center;

  ${({ $isHeroSection }) => $isHeroSection && css`
    ${Typography.modVariant('display-h-regular')};

    @media ${mqMin('primitives_breakpoint_l_min')} {
      ${Typography.modVariant('display-g-regular')};
    }
  `
  }
`

const RatingLabel = styled(Typography)<{ $isHeroSection: boolean }>`
  ${Typography.modVariant('display-g-regular')};
  color: ${tok('primitives_color_success_500')};
  display: flex;
  align-items: center;

  ${({ $isHeroSection }) => $isHeroSection && css`
    ${Typography.modVariant('display-h-regular')};

    @media ${mqMin('primitives_breakpoint_l_min')} {
      ${Typography.modVariant('display-g-regular')};
    }
  `
  }

`

const NumberOfReviewsText = styled(Typography)`
  ${Typography.modVariant('body-m-regular-snug')};
  color: ${tok('semantics_content_color')};
`
const StyledButton = styled.button`
  position: relative;
  height: ${space('primitives_space_16')};
  margin-top: ${space('primitives_space_4')};
  padding-left: ${space('primitives_space_6')};
  color: ${tok('semantics_content_secondary_color')};
  z-index: 1;
  display: flex;

  ${focusOutline()}
`
const Wrapper = styled.div`
  display: flex;
  align-items: center;
`

export default function ReviewsSummary({ isHeroSection = false, reviewsAmount }: { isHeroSection?: boolean, reviewsAmount: number }) {
  const intl = useIntl()
  const ratingAverage = useCMSPageField('userReviews.ratingAverage')

  return (
    <Wrapper>
      <RatingBadgeWrapper>
        <RatingBadge $isHeroSection={isHeroSection}>{ratingAverage}</RatingBadge>
      </RatingBadgeWrapper>
      <HeaderRightSection>
        <RatingLabel $isHeroSection={isHeroSection}>
          {intl.formatMessage({ id: getUserRatingLabelIntlId(ratingAverage) })}
          <Sheet.Root>
            <Sheet.Trigger>
              <StyledButton aria-label={intl.formatMessage({ id: 'reviews_legal_information' })}>
                <Icon glyph="InformationInCircle" size={16} />
              </StyledButton>
            </Sheet.Trigger>
            <Sheet.Body side="right">
              <Sheet.Header closeLabel={intl.formatMessage({ id: 'close' })} titleLabel={intl.formatMessage({ id: 'reviews_legal_disclaimer_title' })} />
              <Sheet.Content>
                {/* eslint-disable-next-line react-dom/no-dangerously-set-innerhtml -- Content comes from translation and is controlled */}
                <Typography><div dangerouslySetInnerHTML={{ __html: intl.formatMessage({ id: 'reviews_legal_disclaimer_html' }) }} /></Typography>
              </Sheet.Content>
            </Sheet.Body>
          </Sheet.Root>
        </RatingLabel>
        <NumberOfReviewsText>
          {intl.formatMessage({ id: 'reviews_score_amount_label' }, { amount: reviewsAmount })}
        </NumberOfReviewsText>
      </HeaderRightSection>
    </Wrapper>
  )
}
